import React from 'react';
import { absoluteUrl } from '../api/mockApi';

/**
 * Renders a person's uploaded documents.
 *
 * Two shapes exist in the database:
 *   - {url, filename, originalName, size}  — uploaded properly by the current app
 *   - "file:///data/user/0/.../cache/…"    — a device path stored by an older app
 *     build that never actually uploaded. The file only ever existed in that
 *     phone's cache, so it is gone; show it as unavailable rather than offering
 *     a download that cannot work.
 */
const isUsable = (att) => Boolean(att && typeof att === 'object' && att.url);

const prettySize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const Attachments = ({ attachments, onDownload }) => {
  const list = Array.isArray(attachments) ? attachments : [];
  if (!list.length) {
    return <p className="empty-state">No documents uploaded</p>;
  }

  const broken = list.filter(a => !isUsable(a)).length;

  return (
    <div>
      {list.map((att, i) => {
        if (!isUsable(att)) {
          return (
            <div className="doc-row" key={`broken-${i}`}>
              <div className="who">
                <span className="nearest-name" style={{ color: '#999' }}>Document {i + 1}</span>
                <span className="nearest-sub">Not available — never reached the server</span>
              </div>
              <span className="distance-badge outside">Unavailable</span>
            </div>
          );
        }
        const href = absoluteUrl(att.url);
        const name = att.originalName || att.filename || `Document ${i + 1}`;
        const size = prettySize(att.size);
        return (
          <div className="doc-row" key={att.url || i}>
            <div className="who">
              <span className="nearest-name">{name}</span>
              <span className="nearest-sub">{size}</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <a className="btn btn-outline btn-sm" href={href} target="_blank" rel="noopener noreferrer">
                View
              </a>
              <button className="btn btn-outline btn-sm" onClick={() => onDownload(att.url, name)}>
                Download
              </button>
            </div>
          </div>
        );
      })}
      {broken > 0 && (
        <p className="empty-state" style={{ marginTop: 10 }}>
          {broken} document{broken === 1 ? ' was' : 's were'} scanned on an older app version that did
          not upload them. Ask the driver to re-scan from the current app.
        </p>
      )}
    </div>
  );
};

export default Attachments;
